import { Sparkles, Loader2, X, Pencil, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { humanError } from "@/lib/api";

const NET_LABEL = { instagram: "Instagram", linkedin: "LinkedIn", facebook: "Facebook", x: "X (Twitter)" };

/**
 * Shows the per-network "Learning Loop" nudge computed from the tenant's own
 * published posts. Lets the user Apply (into current draft brief), Edit, or
 * Dismiss. If apply/dismiss/edit callbacks are passed, buttons are shown.
 */
export function InsightsCard({ network, onApply, compact = false }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  async function load() {
    if (!network) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/insights/${network}`);
      setInsight(data);
      setDraft(data?.edited_text || data?.text || "");
    } catch {
      setInsight(null);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [network]);

  async function patch(payload) {
    try {
      const { data } = await api.patch(`/insights/${network}`, payload);
      setInsight({ ...insight, ...data });
      return data;
    } catch (e) { toast.error(humanError(e)); }
  }

  async function dismiss() { await patch({ dismissed: true }); toast.success("Insight dismissed"); }
  async function undismiss() { await patch({ dismissed: false }); }
  async function saveEdit() {
    await patch({ edited_text: draft });
    setEditing(false);
    toast.success("Insight updated");
  }

  if (loading) {
    return (
      <div className={`rounded-xl border border-[#E4E9EF] bg-white ${compact ? "p-3" : "p-5"} animate-pulse`} data-testid="insights-card-loading">
        <div className="h-4 w-32 rounded bg-[#F6F8FA]" />
        <div className="mt-3 h-10 rounded bg-[#F6F8FA]" />
      </div>
    );
  }
  if (!insight) return null;

  const text = insight.edited_text || insight.text || "";
  const notEnough = !insight.available;

  return (
    <div
      data-testid={`insights-card-${network}`}
      className={`relative overflow-hidden rounded-xl border bg-white ${compact ? "p-3" : "p-5"} ${
        insight.dismissed ? "border-dashed border-[#E4E9EF]" : "border-[#E4E9EF]"
      } hs-card-shadow`}
    >
      {/* Mint flourish */}
      <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#E8FBF1] opacity-60" />

      <div className="relative flex items-start justify-between">
        <div className="flex items-start gap-2">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EEF3FB] text-[#1F5FD8]">
            <Sparkles className="h-4 w-4" />
          </span>
          <div>
            <div className="text-sm font-semibold text-[#0B2B33]">
              Learning Loop · <span className="capitalize">{NET_LABEL[network] || network}</span>
            </div>
            <div className="text-[11px] text-[#8A97A0]">
              {insight.sample_size || 0} published posts analysed
            </div>
          </div>
        </div>
        {!notEnough && !insight.dismissed ? (
          <button
            data-testid={`insight-dismiss-${network}`}
            onClick={dismiss}
            className="rounded-full p-1 text-[#8A97A0] hover:bg-[#F6F8FA]"
            title="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {notEnough ? (
        <p className="relative mt-3 text-sm text-[#43535E]">
          Not enough published data yet. Publish a few more posts on {NET_LABEL[network] || network} and we'll start learning what works.
        </p>
      ) : insight.dismissed ? (
        <div className="relative mt-3 flex items-center justify-between gap-3">
          <p className="text-sm italic text-[#8A97A0] line-through">{text}</p>
          <button onClick={undismiss} className="text-xs font-semibold text-[#1F5FD8] hover:underline">Bring back</button>
        </div>
      ) : (
        <>
          {editing ? (
            <div className="relative mt-3">
              <textarea
                data-testid={`insight-edit-${network}`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-[#E4E9EF] p-2 text-sm"
              />
              <div className="mt-2 flex gap-2">
                <button onClick={saveEdit} data-testid={`insight-save-${network}`} className="hs-btn-primary text-xs">
                  <Check className="mr-1 -mt-0.5 inline h-3 w-3" /> Save
                </button>
                <button onClick={() => { setEditing(false); setDraft(text); }} className="rounded-full border border-[#E4E9EF] px-3 py-1 text-xs">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div className="relative mt-3 space-y-1.5">
                {(insight.hints || []).map((h, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-[#0B2B33]">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#16A46B]" />
                    <span>{h}</span>
                  </div>
                ))}
                {insight.edited_text ? (
                  <div className="mt-2 rounded-lg bg-[#F6F8FA] p-2 text-xs text-[#43535E]">
                    <span className="font-semibold">Your note:</span> {insight.edited_text}
                  </div>
                ) : null}
              </div>
              <div className="relative mt-4 flex flex-wrap gap-2">
                {onApply ? (
                  <button
                    data-testid={`insight-apply-${network}`}
                    onClick={() => onApply(text)}
                    className="hs-btn-primary text-xs"
                  >
                    <Sparkles className="mr-1 -mt-0.5 inline h-3 w-3" /> Apply to brief
                  </button>
                ) : null}
                <button
                  data-testid={`insight-edit-open-${network}`}
                  onClick={() => setEditing(true)}
                  className="rounded-full border border-[#E4E9EF] px-3 py-1 text-xs font-semibold text-[#0B2B33] hover:bg-[#F6F8FA]"
                >
                  <Pencil className="mr-1 -mt-0.5 inline h-3 w-3" /> Edit
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
